import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../auth/useAuth";
import { useVolWorkstationStore } from "../../../store/volWorkstationStore";
import type { VolWorkstationPublicLayout } from "../../../api/volWorkstationLayoutsRepo";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../../components/ui/DropdownMenu";

export function VolPublicLayoutMenu({ curveId }: { curveId: string }) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("admin") || user?.claims?.tier === "admin";
  const userKey = user?.userId ?? user?.username ?? "";

  const listPublicLayouts = useVolWorkstationStore((s) => s.listPublicLayouts);
  const savePublicLayout = useVolWorkstationStore((s) => s.savePublicLayout);
  const loadPublicLayout = useVolWorkstationStore((s) => s.loadPublicLayout);
  const deletePublicLayout = useVolWorkstationStore((s) => s.deletePublicLayout);

  const [layouts, setLayouts] = useState<VolWorkstationPublicLayout[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const rows = await listPublicLayouts(curveId);
      setLayouts(rows);
    } catch {
      setLayouts([]);
    }
  }, [curveId, listPublicLayouts]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const promptSave = async () => {
    const name = window.prompt("Public layout name:", editingId ? layouts.find((l) => l.layout_id === editingId)?.name : "Default vol layout");
    if (!name?.trim()) return;
    try {
      await savePublicLayout(curveId, name.trim(), true, editingId ?? undefined);
      setEditingId(null);
      await refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Save failed");
    }
  };

  const promptLoad = async (layoutId: string) => {
    try {
      await loadPublicLayout(curveId, layoutId);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Load failed");
    }
  };

  const tryDelete = async (layout: VolWorkstationPublicLayout) => {
    if (!window.confirm(`Delete public layout "${layout.name}"?`)) return;
    try {
      await deletePublicLayout(layout.layout_id, layout.created_by, isAdmin);
      if (editingId === layout.layout_id) setEditingId(null);
      await refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const canDelete = (layout: VolWorkstationPublicLayout) =>
    isAdmin || layout.created_by === userKey;

  return (
    <>
      <DropdownMenuSeparator />
      <div className="terminal-menu-section">
        <div className="terminal-menu-section__header">Public layouts (Postgres)</div>
        <DropdownMenuItem
          className="terminal-menu-section__item !flex !items-center !justify-between !px-3 !py-1.5"
          onSelect={() => void promptSave()}
        >
          <span>{editingId ? "Update selected layout" : "Save layout to Postgres"}</span>
          <span className="terminal-menu-section__item-hint">💾</span>
        </DropdownMenuItem>
        {layouts.length === 0 ? (
          <div className="px-3 py-1 text-[10px] text-muted">No saved public layouts</div>
        ) : (
          layouts.slice(0, 12).map((layout) => (
            <div key={layout.layout_id} className="flex items-center gap-1 px-1">
              <DropdownMenuItem
                className="terminal-menu-section__item !flex-1 !px-2 !py-1"
                onSelect={() => void promptLoad(layout.layout_id)}
              >
                <span className="truncate" title={`${layout.name} · ${layout.created_by}`}>
                  {layout.name}
                </span>
              </DropdownMenuItem>
              <button
                type="button"
                className="px-1 text-[10px] text-muted hover:text-neutral-200"
                title="Select for update"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(layout.layout_id);
                }}
              >
                ✎
              </button>
              {canDelete(layout) ? (
                <button
                  type="button"
                  className="px-1 text-[10px] text-muted hover:text-red-400"
                  title="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    void tryDelete(layout);
                  }}
                >
                  ×
                </button>
              ) : null}
            </div>
          ))
        )}
      </div>
    </>
  );
}
